/**
 * Allowlist sanitizer for HTML written in the rich-text editor (TinyMCE). Angular's own sanitizer drops every
 * `style` attribute, which would lose colours, font sizes and alignment; this one keeps exactly the formatting the
 * editor produces and removes everything else, so the result can be bound with bypassSecurityTrustHtml.
 */

const ALLOWED_TAGS = new Set([
  'A', 'B', 'BLOCKQUOTE', 'BR', 'CAPTION', 'CODE', 'COL', 'COLGROUP', 'DIV', 'EM', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'HR', 'I', 'LI', 'MARK', 'OL', 'P', 'PRE', 'S', 'SPAN', 'STRIKE', 'STRONG', 'SUB', 'SUP', 'TABLE', 'TBODY', 'TD',
  'TFOOT', 'TH', 'THEAD', 'TR', 'U', 'UL',
]); // prettier-ignore

// Removed together with their content; any other unknown element is unwrapped and keeps its text.
const DROPPED_TAGS = new Set([
  'BUTTON', 'EMBED', 'FORM', 'IFRAME', 'IMG', 'INPUT', 'LINK', 'MATH', 'META', 'NOSCRIPT', 'OBJECT', 'SCRIPT',
  'SELECT', 'STYLE', 'SVG', 'TEMPLATE', 'TEXTAREA', 'VIDEO', 'AUDIO',
]); // prettier-ignore

const ALLOWED_ATTRIBUTES: Record<string, string[]> = {
  A: ['href', 'title', 'target'],
  COL: ['span'],
  COLGROUP: ['span'],
  OL: ['start', 'type'],
  TABLE: ['border', 'cellpadding', 'cellspacing'],
  TD: ['colspan', 'rowspan'],
  TH: ['colspan', 'rowspan', 'scope'],
};

const ALLOWED_CSS = new Set([
  'background-color', 'border', 'border-collapse', 'border-color', 'border-style', 'border-width', 'color',
  'font-size', 'font-style', 'font-weight', 'height', 'list-style-type', 'margin-left', 'padding-left', 'text-align',
  'text-decoration', 'vertical-align', 'width',
]); // prettier-ignore

const SAFE_URL = /^(https?:|mailto:|tel:|#|\/)/i;
const UNSAFE_CSS_VALUE = /url\s*\(|expression\s*\(|javascript:|@import|behavior\s*:/i;

export function sanitizeRichText(html: string | null | undefined): string {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  cleanChildren(doc.body);
  return doc.body.innerHTML;
}

function cleanChildren(parent: Element): void {
  for (const node of Array.from(parent.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) continue;
    if (node.nodeType !== Node.ELEMENT_NODE) {
      node.remove(); // comments, processing instructions
      continue;
    }
    const element = node as Element;
    const tag = element.tagName.toUpperCase();
    if (DROPPED_TAGS.has(tag)) {
      element.remove();
      continue;
    }
    cleanChildren(element);
    if (!ALLOWED_TAGS.has(tag)) {
      element.replaceWith(...Array.from(element.childNodes));
      continue;
    }
    cleanAttributes(element, tag);
  }
}

function cleanAttributes(element: Element, tag: string): void {
  const allowed = ALLOWED_ATTRIBUTES[tag] ?? [];
  for (const attribute of Array.from(element.attributes)) {
    const name = attribute.name.toLowerCase();
    if (name === 'style') {
      const style = cleanStyle(attribute.value);
      if (style) element.setAttribute('style', style);
      else element.removeAttribute('style');
    } else if (!allowed.includes(name)) {
      element.removeAttribute(attribute.name);
    } else if (name === 'href' && !SAFE_URL.test(attribute.value.trim())) {
      element.removeAttribute(attribute.name);
    }
  }
  if (tag === 'A' && element.getAttribute('target')) {
    element.setAttribute('target', '_blank');
    element.setAttribute('rel', 'noopener noreferrer');
  }
}

function cleanStyle(style: string): string {
  return style
    .split(';')
    .map((declaration) => {
      const index = declaration.indexOf(':');
      if (index < 0) return null;
      const property = declaration.slice(0, index).trim().toLowerCase();
      const value = declaration.slice(index + 1).trim();
      if (!ALLOWED_CSS.has(property) || !value || UNSAFE_CSS_VALUE.test(value)) return null;
      return `${property}: ${value}`;
    })
    .filter(Boolean)
    .join('; ');
}
